import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { fetchWithAuth } from '../../utils/apiAuth';
import config from '../../config';

const CouponCodeChecker = () => {
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  const apiBaseUrl = config.getApiUrl();

  const handleSearch = async () => {
    if (!couponCode.trim()) {
      setError('Please enter a coupon code');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResults(null);

      // Fetch all subscriptions and filter by coupon code
      const data = await fetchWithAuth(`${apiBaseUrl}/api/subscriptions/all`);
      
      // Filter subscriptions by coupon code (case-insensitive)
      const filteredSubscriptions = data.filter(sub => 
        sub.couponCode && 
        sub.couponCode.toLowerCase() === couponCode.trim().toLowerCase()
      );

      setResults({
        subscriptions: filteredSubscriptions,
        totalCount: filteredSubscriptions.length,
        couponCode: couponCode.trim()
      });
    } catch (err) {
      console.error('Error fetching coupon usage:', err);
      setError(err.message || 'Failed to fetch coupon usage data');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID':
      case 'active':
        return 'success';
      case 'FAILED':
      case 'cancelled':
        return 'error';
      case 'INITIATED':
      case 'created':
      case 'authenticated':
        return 'warning';
      case 'halted':
      case 'pending':
        return 'info';
      case 'expired':
      case 'completed':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{ mb: { xs: 2, sm: 4 }, fontSize: { xs: '1.35rem', sm: '2.125rem' } }}
      >
        Coupon Code Usage Checker
      </Typography>

      {/* Search Section */}
      <Card sx={{ maxWidth: 800, mx: 'auto', mb: 4 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 }, '&:last-child': { pb: { xs: 2, sm: 3, md: 4 } } }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Check Coupon Code Usage
          </Typography>
          
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              alignItems: { xs: 'stretch', sm: 'flex-start' }
            }}
          >
            <TextField
              fullWidth
              label="Coupon Code"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter coupon code (e.g., SUMMER2024)"
              disabled={loading}
              error={!!error}
              helperText={error || 'Enter the coupon code to check its usage'}
            />
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSearch}
              disabled={loading || !couponCode.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ minWidth: { xs: '100%', sm: 120 }, height: 56, flexShrink: 0 }}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Results Section */}
      {results && (
        <>
          {/* Summary Card */}
          <Box sx={{ mb: 4 }}>
            <Paper sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                Summary for Coupon Code: <Chip label={results.couponCode} color="primary" />
              </Typography>
              <Box sx={{ display: 'flex', gap: { xs: 2, sm: 4 }, mt: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Subscriptions Using This Code
                  </Typography>
                  <Typography variant="h3" color="primary" sx={{ fontSize: { xs: '1.75rem', sm: '3rem' } }}>
                    {results.totalCount}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Active Subscriptions
                  </Typography>
                  <Typography variant="h3" color="success.main" sx={{ fontSize: { xs: '1.75rem', sm: '3rem' } }}>
                    {results.subscriptions.filter(s => s.status === 'PAID' || s.status === 'active').length}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Pending/Initiated
                  </Typography>
                  <Typography variant="h3" color="warning.main" sx={{ fontSize: { xs: '1.75rem', sm: '3rem' } }}>
                    {results.subscriptions.filter(s => s.status === 'INITIATED' || s.status === 'created').length}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

          {/* Subscriptions Table */}
          {results.totalCount > 0 ? (
            <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table sx={{ minWidth: 650 }} aria-label="coupon usage table">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Customer Name</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                    <TableCell><strong>Period</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Subscription ID</strong></TableCell>
                    <TableCell><strong>Payment ID</strong></TableCell>
                    <TableCell><strong>Created At</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.subscriptions.map((subscription) => (
                    <TableRow key={subscription._id}>
                      <TableCell>{subscription.customerName}</TableCell>
                      <TableCell>{subscription.email}</TableCell>
                      <TableCell>{subscription.phone}</TableCell>
                      <TableCell>
                        <Chip
                          label={subscription.period || 'N/A'}
                          color={subscription.period === 'monthly' ? 'primary' : subscription.period === 'yearly' ? 'secondary' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={subscription.status}
                          color={getStatusColor(subscription.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {subscription.subscriptionId || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                          {subscription.paymentDetails?.razorpay_payment_id || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {subscription.createdAt ? new Date(subscription.createdAt).toLocaleString() : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" sx={{ maxWidth: 800, mx: 'auto' }}>
              No subscriptions found using coupon code: <strong>{results.couponCode}</strong>
            </Alert>
          )}
        </>
      )}

      {/* Initial State Message */}
      {!results && !loading && !error && (
        <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
          <Typography variant="h6" color="text.secondary">
            Enter a coupon code above to check its usage
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            You'll see the total number of subscriptions and detailed information
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CouponCodeChecker;
